import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Btn, Msg, panelGet, panelPost } from '../kit';
import { Modal, MultiSelect } from '../email/emailKit';

/**
 * User lifecycle + custom tags — the vocabulary and the controls every admin
 * tab that lists people shares (backend: Backend/user_tags.py, section "tags").
 *
 *  · Lifecycle is computed live on the server, never stored: the five billing
 *    states renamed for people (explorer / voyager paid / assigned / trial /
 *    trial lapsed) plus "prospect · invited" for an account that has never
 *    signed in. Colours are fixed here so a pill reads the same in every tab.
 *  · Custom tags are admin-assigned (user_tag_defs + user_tags); each carries
 *    its own colour.
 *  · The filter travels as the same six query params everywhere (lifecycle,
 *    tags, tags_all, not_tags, invite_sent, signed_in) — tagFilterParams()
 *    builds them, so every tab filters with one vocabulary.
 */

export const TAGS_SECTION = 'tags';

// ---------------------------------------------------------------------------
// Lifecycle vocabulary
// ---------------------------------------------------------------------------
export type LifecycleKey =
  'explorer' | 'voyager_paid' | 'voyager_assigned' | 'voyager_trial' | 'voyager_trial_lapsed' | 'prospect';

export const LIFECYCLES: { key: LifecycleKey; label: string; color: string; hint: string }[] = [
  { key: 'explorer', label: 'Explorer', color: '#2563EB',
    hint: 'Signed in at least once, no Voyager plan' },
  { key: 'voyager_paid', label: 'Voyager (paid)', color: '#15803D',
    hint: 'Paying Voyager subscription' },
  { key: 'voyager_assigned', label: 'Voyager (assigned)', color: '#7C3AED',
    hint: 'Voyager granted by an admin, no payment' },
  { key: 'voyager_trial', label: 'Voyager trial', color: '#C2410C',
    hint: 'Trial in progress' },
  { key: 'voyager_trial_lapsed', label: 'Voyager trial lapsed', color: '#B91C1C',
    hint: 'Trial ended without converting' },
  { key: 'prospect', label: 'Prospect · invited', color: '#64748B',
    hint: 'Account exists but has never signed in (OTP, Google or LinkedIn)' },
];
export const LIFECYCLE_KEYS: string[] = LIFECYCLES.map(l => l.key);
export const LIFECYCLE_LABEL: Record<string, string> =
  Object.fromEntries(LIFECYCLES.map(l => [l.key, l.label]));
export const LIFECYCLE_COLOR: Record<string, string> =
  Object.fromEntries(LIFECYCLES.map(l => [l.key, l.color]));

/** billing_service.user_state → lifecycle, for payloads still in the old words. */
export const STATE_TO_LIFECYCLE: Record<string, LifecycleKey> = {
  explorer: 'explorer', trial_active: 'voyager_trial', trial_lapsed: 'voyager_trial_lapsed',
  voyager: 'voyager_paid', voyager_assigned: 'voyager_assigned',
};
/** A lifecycle key from either vocabulary ('' when there is none). */
export const toLifecycle = (v: any): string => {
  const s = v === null || v === undefined ? '' : String(v);
  return LIFECYCLE_LABEL[s] ? s : (STATE_TO_LIFECYCLE[s] || s);
};

// ---------------------------------------------------------------------------
// Account origin (Backend/account_origin.py): the person signed up, or the
// team added the account — recorded at creation, worked out for older ones.
// ---------------------------------------------------------------------------
export const ORIGINS = [
  { key: 'self', label: 'Self sign-up', color: '#0F766E',
    hint: 'Made by the person: signed up with an e-mail code, LinkedIn / Google or the sign-up form, or asked for an invite' },
  { key: 'admin', label: 'Added by admin', color: '#6B7280',
    hint: 'Made by the team: admin panel, bulk add or People import, without the person signing in' },
] as const;
export const ORIGIN_LABEL: Record<string, string> = Object.fromEntries(ORIGINS.map(o => [o.key, o.label]));
const ORIGIN_COLOR: Record<string, string> = Object.fromEntries(ORIGINS.map(o => [o.key, o.color]));

/** "Self sign-up" / "Added by admin", with how the account was made on hover. */
export const OriginPill: React.FC<{ value?: string | null; label?: string | null; via?: string | null }> =
  ({ value, label, via }) => {
  if (!value || !ORIGIN_LABEL[value]) return null;
  const color = ORIGIN_COLOR[value] || '#6B7280';
  return (
    <span className="inline-flex items-center rounded-full px-2 py-0.5 text-[11px] whitespace-nowrap border"
      style={{ color, borderColor: color }}
      title={`${label || ORIGIN_LABEL[value] || value}${via ? ` — ${via}` : ''}`}>
      {value === 'self' ? 'Self sign-up' : 'Added by admin'}
    </span>
  );
};

/** Slug rule of user_tag_defs.key; the lifecycle words are reserved. */
export const RESERVED_TAG_KEYS = [...LIFECYCLE_KEYS, 'invited', ...Object.keys(STATE_TO_LIFECYCLE)];
export const TAG_COLORS = ['#2563EB', '#0891B2', '#059669', '#65A30D', '#CA8A04', '#EA580C',
  '#DC2626', '#DB2777', '#9333EA', '#4F46E5', '#475569', '#78716C'];

const safeHex = (c: any, fallback = '#64748B'): string =>
  typeof c === 'string' && /^#[0-9a-fA-F]{6}$/.test(c.trim()) ? c.trim() : fallback;

export type TagRef = { key: string; label: string; color?: string };

// ---------------------------------------------------------------------------
// Pills
// ---------------------------------------------------------------------------
/** The account's lifecycle, in its fixed colour. Accepts the old state words. */
export const LifecyclePill: React.FC<{
  value?: string | null; subtag?: string | null; label?: string | null; title?: string;
}> = ({ value, subtag, label, title }) => {
  const key = toLifecycle(value);
  if (!key) return <span className="text-grayscale-1100">—</span>;
  const color = safeHex(LIFECYCLE_COLOR[key]);
  let text = LIFECYCLE_LABEL[key] || label || key;
  if (key === 'prospect' && subtag && subtag !== 'invited') text = `Prospect · ${subtag}`;
  const hint = title || LIFECYCLES.find(l => l.key === key)?.hint || '';
  return (
    <span title={hint}
      style={{ color, backgroundColor: `${color}14`, borderColor: `${color}59` }}
      className="inline-flex items-center text-[11px] font-medium rounded-full border px-2 py-0.5 whitespace-nowrap">
      {text}
    </span>
  );
};

/** One custom tag: a dot in the tag's colour and its label. */
export const TagPill: React.FC<{ tag: TagRef; onRemove?: () => void; busy?: boolean }> =
  ({ tag, onRemove, busy }) => {
  const color = safeHex(tag.color);
  return (
    <span title={tag.key}
      style={{ backgroundColor: `${color}12`, borderColor: `${color}66` }}
      className="inline-flex items-center gap-1 text-[11px] text-grayscale-1300 rounded-full border pl-1.5 pr-2 py-0.5 whitespace-nowrap">
      <span aria-hidden className="inline-block w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: color }} />
      {tag.label || tag.key}
      {onRemove && (
        <button type="button" onClick={onRemove} disabled={busy} title={`Remove “${tag.label || tag.key}”`}
          className="ml-0.5 -mr-1 w-4 h-4 rounded-full leading-none text-grayscale-1100 hover:bg-grayscale-200 hover:text-red-600 disabled:opacity-40">
          ×
        </button>
      )}
    </span>
  );
};

/** An account's custom tags. `max` folds the rest into "+n". */
export const TagPills: React.FC<{
  tags?: TagRef[] | null; max?: number; onRemove?: (key: string) => void; empty?: React.ReactNode;
  /** One line (table cells); the default wraps. */
  nowrap?: boolean;
}> = ({ tags, max, onRemove, empty, nowrap }) => {
  const list = (Array.isArray(tags) ? tags : []).filter(t => t && t.key);
  if (!list.length) return <>{empty ?? null}</>;
  const shown = max && list.length > max ? list.slice(0, max) : list;
  const rest = list.slice(shown.length);
  return (
    <span className={`inline-flex items-center gap-1 ${nowrap ? 'whitespace-nowrap' : 'flex-wrap'}`}>
      {shown.map(t => (
        <TagPill key={t.key} tag={t} onRemove={onRemove ? () => onRemove(t.key) : undefined} />
      ))}
      {rest.length > 0 && (
        <span className="text-[11px] text-grayscale-1100" title={rest.map(t => t.label || t.key).join(', ')}>
          +{rest.length}
        </span>
      )}
    </span>
  );
};

/** Lifecycle + custom tags together, for one table cell. Reads the fields
 *  user_tags.decorate adds to a row (lifecycle, lifecycle_label, subtag, tags). */
export const UserTagCells: React.FC<{ row: Record<string, any> | null | undefined; max?: number }> =
  ({ row, max = 3 }) => {
  const auto = Array.isArray(row?.auto_tags) ? row!.auto_tags : [];
  return (
    <span className="inline-flex items-center gap-1 whitespace-nowrap">
      <LifecyclePill value={row?.lifecycle ?? null} subtag={row?.subtag} label={row?.lifecycle_label} />
      <OriginPill value={row?.account_origin} label={row?.account_origin_label} via={row?.account_origin_via_label} />
      {row?.persona && (
        <span className="inline-flex items-center rounded-full border border-grayscale-400 px-2 py-0.5 text-[11px] text-grayscale-1200"
          title="Primary persona">{row?.persona_label || PERSONA_LABEL[row.persona] || row.persona}</span>
      )}
      <TagPills tags={row?.tags} max={max} nowrap />
      {auto.length > 0 && (
        <span className="text-[11px] text-grayscale-1100 border border-dashed border-grayscale-400 rounded-full px-1.5 py-0.5"
          title={`Automatic: ${auto.map((t: any) => t.label).join(', ')}`}>
          {auto.length === 1 ? auto[0].label : `${auto[0].label} +${auto.length - 1}`}
        </span>
      )}
    </span>
  );
};

/** For DataTable's `render` map: `render={{ lifecycle: tagCell }}`. */
export const tagCell = (row: Record<string, any>) => <UserTagCells row={row} />;
/** Plain text of a row's tags, for CSV columns. */
export const tagText = (row: Record<string, any> | null | undefined): string =>
  [...(row?.account_origin_label ? [{ label: row.account_origin_label }] : []),
   ...(Array.isArray(row?.tags) ? row!.tags : []), ...(Array.isArray(row?.auto_tags) ? row!.auto_tags : [])]
    .map((t: any) => t?.label || t?.key).filter(Boolean).join(', ');

// ---------------------------------------------------------------------------
// Filter value + the standard params
// ---------------------------------------------------------------------------
export type TriState = '' | '1' | '0';
/** The dates a filter ranges over (user_tags.DATE_FACTS). */
export const DATE_FACTS = [
  { key: 'signed_up', label: 'Signed up' },
  { key: 'last_login', label: 'Last logged in' },
  { key: 'last_search', label: 'Last searched' },
  { key: 'last_chat', label: 'Last AI chat' },
  { key: 'invited', label: 'Invited (first)' },
  { key: 'last_activity', label: 'Last activity' },
  { key: 'last_emailed', label: 'Last e-mailed' },
] as const;
export type DateFact = typeof DATE_FACTS[number]['key'];
/** from / to: YYYY-MM-DD (inclusive); empty '1' = no such date, '0' = has one. */
export type DateRangeValue = { from?: string; to?: string; empty?: TriState };
export const PERSONAS = [
  { key: 'academia', label: 'Academia' }, { key: 'industry', label: 'Industry' },
  { key: 'investor', label: 'Investor' }, { key: 'consultant', label: 'Consultant' },
  { key: 'none', label: 'No persona' },
] as const;
export const PERSONA_LABEL: Record<string, string> = Object.fromEntries(PERSONAS.map(p => [p.key, p.label]));
export type TagFilterValue = {
  lifecycle: string[];
  /** Custom and automatic ("auto-…") tag keys. */
  tags: string[];
  /** any → `tags` (has ANY), all → `tags_all` (has ALL). */
  tagMode: 'any' | 'all';
  /** → `not_tags` (has NONE of these). */
  notTags: string[];
  inviteSent: TriState;
  signedIn: TriState;
  /** Primary persona keys (spec v1.2), 'none' = not set. */
  persona: string[];
  /** Profile completion bounds, '' = none. */
  profileMin: string;
  profileMax: string;
  dates: Partial<Record<DateFact, DateRangeValue>>;
  /** Account origin: '' any, 'self' or 'admin'. Optional, so filter values
   *  saved before it existed still read. */
  origin?: string;
};

export const EMPTY_TAG_FILTER: TagFilterValue = {
  lifecycle: [], tags: [], tagMode: 'any', notTags: [], inviteSent: '', signedIn: '',
  persona: [], profileMin: '', profileMax: '', dates: {}, origin: '',
};

/** The filter as user_tags.parse_filter reads it — only what is set, lists comma-joined. */
export const tagFilterParams = (v: TagFilterValue): Record<string, string> => {
  const p: Record<string, string> = {};
  if (v.lifecycle.length) p.lifecycle = v.lifecycle.join(',');
  if (v.tags.length) p[v.tagMode === 'all' ? 'tags_all' : 'tags'] = v.tags.join(',');
  if (v.notTags.length) p.not_tags = v.notTags.join(',');
  if (v.inviteSent) p.invite_sent = v.inviteSent;
  if (v.signedIn) p.signed_in = v.signedIn;
  if (v.persona?.length) p.persona = v.persona.join(',');
  // account_origin on the wire: "origin" is a site-visit filter already.
  if (v.origin) p.account_origin = v.origin;
  if (v.profileMin) p.profile_min = v.profileMin;
  if (v.profileMax) p.profile_max = v.profileMax;
  Object.entries(v.dates || {}).forEach(([k, d]) => {
    if (!d) return;
    if (d.from) p[`${k}_from`] = d.from;
    if (d.to) p[`${k}_to`] = d.to;
    if (d.empty) p[`${k}_empty`] = d.empty;
  });
  return p;
};

const datesActive = (v: TagFilterValue) =>
  Object.values(v.dates || {}).some(d => !!(d && (d.from || d.to || d.empty)));

export const tagFilterActive = (v: TagFilterValue): boolean =>
  !!(v.lifecycle.length || v.tags.length || v.notTags.length || v.inviteSent || v.signedIn
     || v.persona?.length || v.origin || v.profileMin || v.profileMax || datesActive(v));

const splitList = (s: any): string[] =>
  String(s ?? '').split(',').map(x => x.trim()).filter(Boolean);

/** The reverse of tagFilterParams — for deep links (?lifecycle=…&tags=…). */
export const tagFilterFromParams = (p: Record<string, any>): TagFilterValue => {
  const tri = (x: any): TriState => (x === '1' || x === '0' ? x : '');
  const all = splitList(p.tags_all);
  const dates: Partial<Record<DateFact, DateRangeValue>> = {};
  DATE_FACTS.forEach(({ key }) => {
    const d: DateRangeValue = {};
    if (p[`${key}_from`]) d.from = String(p[`${key}_from`]);
    if (p[`${key}_to`]) d.to = String(p[`${key}_to`]);
    if (tri(p[`${key}_empty`])) d.empty = tri(p[`${key}_empty`]);
    if (d.from || d.to || d.empty) dates[key] = d;
  });
  return {
    lifecycle: splitList(p.lifecycle).map(toLifecycle),
    tags: all.length ? all : splitList(p.tags),
    tagMode: all.length ? 'all' : 'any',
    notTags: splitList(p.not_tags),
    inviteSent: tri(p.invite_sent),
    signedIn: tri(p.signed_in),
    persona: splitList(p.persona),
    origin: p.account_origin === 'self' || p.account_origin === 'admin' ? p.account_origin : '',
    profileMin: p.profile_min ? String(p.profile_min) : '',
    profileMax: p.profile_max ? String(p.profile_max) : '',
    dates,
  };
};

/** The filter in words, for summaries and confirm dialogs. */
export const describeTagFilter = (v: TagFilterValue, tagLabel: (k: string) => string = k => k): string[] => {
  const out: string[] = [];
  if (v.lifecycle.length) out.push(`Lifecycle: ${v.lifecycle.map(k => LIFECYCLE_LABEL[k] || k).join(', ')}`);
  if (v.tags.length) {
    out.push(`${v.tagMode === 'all' ? 'Has all of' : 'Has any of'}: ${v.tags.map(tagLabel).join(', ')}`);
  }
  if (v.notTags.length) out.push(`Has none of: ${v.notTags.map(tagLabel).join(', ')}`);
  if (v.inviteSent) out.push(v.inviteSent === '1' ? 'Invite sent' : 'No invite sent');
  if (v.signedIn) out.push(v.signedIn === '1' ? 'Signed in at least once' : 'Never signed in');
  if (v.persona?.length) out.push(`Persona: ${v.persona.map(k => PERSONA_LABEL[k] || k).join(', ')}`);
  if (v.origin) out.push(ORIGIN_LABEL[v.origin] || v.origin);
  if (v.profileMin || v.profileMax) {
    out.push(`Profile ${v.profileMin ? `≥ ${v.profileMin}%` : ''}${v.profileMin && v.profileMax ? ' and ' : ''}${v.profileMax ? `≤ ${v.profileMax}%` : ''}`);
  }
  DATE_FACTS.forEach(({ key, label }) => {
    const d = v.dates?.[key];
    if (!d) return;
    if (d.empty === '1') out.push(`${label}: never`);
    else if (d.from || d.to) out.push(`${label}: ${d.from || '…'} → ${d.to || 'today'}`);
    else if (d.empty === '0') out.push(`${label}: at some point`);
  });
  return out;
};

/** Client-side version for tabs that filter rows already on screen (rows
 *  decorated by user_tags.decorate). A field the row does not carry never
 *  matches a filter on it, so nothing passes unseen. */
export const matchesTagFilter = (row: Record<string, any>, v: TagFilterValue): boolean => {
  const lc = toLifecycle(row?.lifecycle);
  if (v.lifecycle.length && !v.lifecycle.includes(lc)) return false;
  const keys = new Set<string>([...(Array.isArray(row?.tags) ? row.tags : []), ...(Array.isArray(row?.auto_tags) ? row.auto_tags : [])]
    .map((t: any) => String(t?.key ?? t)));
  if (v.persona?.length && !v.persona.includes(String(row?.persona || 'none'))) return false;
  if (v.origin && row?.account_origin !== v.origin) return false;
  if (v.tags.length) {
    const ok = v.tagMode === 'all' ? v.tags.every(k => keys.has(k)) : v.tags.some(k => keys.has(k));
    if (!ok) return false;
  }
  if (v.notTags.some(k => keys.has(k))) return false;
  const flag = (x: any): boolean | null => (x === true || x === 1 || x === '1' ? true
    : x === false || x === 0 || x === '0' ? false : null);
  if (v.signedIn) {
    const s = flag(row?.signed_in);
    if (s === null || s !== (v.signedIn === '1')) return false;
  }
  if (v.inviteSent) {
    const s = flag(row?.invite_sent);
    if (s === null || s !== (v.inviteSent === '1')) return false;
  }
  return true;
};

// ---------------------------------------------------------------------------
// Options (tag defs + counts), cached across every tab that shows a picker
// ---------------------------------------------------------------------------
export type TagOption = {
  key: string; label: string; color: string; grp?: string; description?: string; n?: number;
  created_by?: string; created_at?: string;
};
export type LifecycleOption = { key: string; label: string; color: string; n?: number; signed_in_n?: number };
export type TagOptions = {
  lifecycle: LifecycleOption[]; tags: TagOption[];
  totals: Record<string, number>; teamExcluded: number | null;
  /** The automatic cohort tags (user_tags.AUTO_TAGS) with counts. */
  autoTags: TagOption[];
  personas: Array<{ key: string; label: string; n?: number }>;
  origins: Array<{ key: string; label: string; n?: number }>;
};

const TTL_MS = 60_000;
type Store = { data: TagOptions | null; error: string; at: number; inflight: Promise<void> | null };
const store: Store = { data: null, error: '', at: 0, inflight: null };
const listeners = new Set<() => void>();
const notify = () => listeners.forEach(f => f());

const rowsOf = (d: any, key: string): any[] =>
  Array.isArray(d) ? d : Array.isArray(d?.[key]) ? d[key] : Array.isArray(d?.rows) ? d.rows : [];

/** One GET of defs + overview, shared by every mounted picker. */
function loadTagOptions(force = false): Promise<void> {
  if (!force && store.data && Date.now() - store.at < TTL_MS) return Promise.resolve();
  if (store.inflight) return store.inflight;
  store.inflight = (async () => {
    const [defs, ov, more] = await Promise.all([
      panelGet(TAGS_SECTION, 'defs'), panelGet(TAGS_SECTION, 'overview'), panelGet(TAGS_SECTION, 'options'),
    ]);
    const autoTags: TagOption[] = (Array.isArray(more.data?.auto_tags) ? more.data.auto_tags : []).map((t: any) => ({
      key: String(t.key), label: String(t.label || t.key), color: safeHex(t.color), grp: 'Automatic',
      description: t.description || '', n: t.n !== undefined ? Number(t.n) : undefined,
    }));
    const personas = (Array.isArray(more.data?.personas) ? more.data.personas : []).map((x: any) => ({
      key: String(x.key), label: String(x.label || x.key), n: x.n !== undefined ? Number(x.n) : undefined,
    }));
    const origins = (Array.isArray(more.data?.origins) ? more.data.origins : []).map((x: any) => ({
      key: String(x.key), label: String(x.label || x.key), n: x.n !== undefined ? Number(x.n) : undefined,
    }));
    const defRows = rowsOf(defs.data, 'rows');
    const ovTags = rowsOf(ov.data, 'tags');
    const nByKey: Record<string, number> = Object.fromEntries(ovTags.map((t: any) => [String(t.key), Number(t.n || 0)]));
    const tags: TagOption[] = (defRows.length ? defRows : ovTags).map((t: any) => ({
      key: String(t.key), label: String(t.label || t.key), color: safeHex(t.color),
      grp: t.grp || '', description: t.description || '',
      n: t.n !== undefined && t.n !== null ? Number(t.n) : nByKey[String(t.key)],
      created_by: t.created_by, created_at: t.created_at,
    }));
    const ovLc: any[] = rowsOf(ov.data, 'lifecycle');
    const lifecycle: LifecycleOption[] = LIFECYCLES.map(l => {
      const hit = ovLc.find(x => toLifecycle(x?.key) === l.key);
      return { key: l.key, label: l.label, color: l.color,
        n: hit ? Number(hit.n || 0) : undefined,
        signed_in_n: hit && hit.signed_in_n !== undefined ? Number(hit.signed_in_n || 0) : undefined };
    });
    const err = defs.error || ov.error;
    if (defs.data || ov.data) {
      store.data = { lifecycle, tags, totals: ov.data?.totals || {},
        teamExcluded: ov.data?.team_excluded_count ?? null, autoTags, personas, origins };
      store.at = Date.now();
    }
    store.error = err;
    store.inflight = null;
    notify();
  })();
  return store.inflight;
}

/** Drop the cache after a write (tag created, renamed, assigned …) and refetch. */
export function invalidateTagOptions() {
  store.at = 0;
  if (listeners.size) loadTagOptions(true);
}

/** Tag defs + lifecycle/tag counts for pickers; cached 60 s across tabs. */
export function useTagOptions() {
  const [, bump] = useState(0);
  useEffect(() => {
    const f = () => bump(x => x + 1);
    listeners.add(f);
    loadTagOptions();
    return () => { listeners.delete(f); };
  }, []);
  const data = store.data;
  const tagByKey = useMemo(
    () => Object.fromEntries([...(data?.autoTags || []), ...(data?.tags || [])].map(t => [t.key, t])) as Record<string, TagOption>,
    [data]);
  return {
    lifecycle: data?.lifecycle || LIFECYCLES.map(l => ({ key: l.key, label: l.label, color: l.color })),
    tags: data?.tags || [],
    autoTags: data?.autoTags || [],
    personas: data?.personas || PERSONAS.map(p => ({ key: p.key, label: p.label })),
    origins: (data?.origins?.length ? data.origins : ORIGINS.map(o => ({ key: o.key, label: o.label }))) as
      Array<{ key: string; label: string; n?: number }>,
    totals: data?.totals || {},
    teamExcluded: data?.teamExcluded ?? null,
    tagByKey,
    loading: !data && !store.error,
    error: store.error,
    reload: () => loadTagOptions(true),
  };
}

// ---------------------------------------------------------------------------
// TagFilter — lifecycle + custom tags (any / all / none) + invite + signed-in
// ---------------------------------------------------------------------------
type FilterPart = 'lifecycle' | 'tags' | 'not_tags' | 'invite_sent' | 'signed_in' | 'persona' | 'origin' | 'dates' | 'profile';

const isoDay = (d: Date) => d.toISOString().slice(0, 10);
const daysAgo = (n: number) => isoDay(new Date(Date.now() - n * 86400000));
/** Presets for one date fact: within the last N days, more than N days ago,
 *  never, or a custom range. */
const DATE_PRESETS: Array<{ key: string; label: string; value: () => DateRangeValue }> = [
  { key: '', label: 'Any time', value: () => ({}) },
  { key: 'l7', label: 'Last 7 days', value: () => ({ from: daysAgo(7) }) },
  { key: 'l30', label: 'Last 30 days', value: () => ({ from: daysAgo(30) }) },
  { key: 'l60', label: 'Last 60 days', value: () => ({ from: daysAgo(60) }) },
  { key: 'l90', label: 'Last 90 days', value: () => ({ from: daysAgo(90) }) },
  { key: 'o7', label: 'More than 7 days ago', value: () => ({ to: daysAgo(8) }) },
  { key: 'o14', label: 'More than 14 days ago', value: () => ({ to: daysAgo(15) }) },
  { key: 'o30', label: 'More than 30 days ago', value: () => ({ to: daysAgo(31) }) },
  { key: 'never', label: 'Never', value: () => ({ empty: '1' }) },
  { key: 'custom', label: 'Custom range…', value: () => ({ from: daysAgo(30), to: isoDay(new Date()) }) },
];
const presetOf = (d?: DateRangeValue): string => {
  if (!d || (!d.from && !d.to && !d.empty)) return '';
  if (d.empty === '1') return 'never';
  const hit = DATE_PRESETS.find(p => p.key && p.key !== 'custom' && p.key !== 'never'
    && JSON.stringify(p.value()) === JSON.stringify({ ...(d.from ? { from: d.from } : {}), ...(d.to ? { to: d.to } : {}) }));
  return hit ? hit.key : 'custom';
};

/** One date fact: a preset, and from / to inputs for a custom range. */
const DateFactFilter: React.FC<{ label: string; value?: DateRangeValue; onChange: (v: DateRangeValue | undefined) => void }> =
  ({ label, value, onChange }) => {
  const preset = presetOf(value);
  return (
    <label className="flex flex-col gap-1 text-xs text-grayscale-1100">
      {label}
      <span className="flex items-center gap-1">
        <select value={preset} className={selectCls}
          onChange={e => { const p = DATE_PRESETS.find(x => x.key === e.target.value); const v = p ? p.value() : {}; onChange(v.from || v.to || v.empty ? v : undefined); }}>
          {DATE_PRESETS.map(p => <option key={p.key} value={p.key}>{p.label}</option>)}
        </select>
        {preset === 'custom' && (
          <>
            <input type="date" value={value?.from || ''} className={selectCls} aria-label={`${label} from`}
              onChange={e => onChange({ ...value, from: e.target.value || undefined, empty: undefined })} />
            <span>→</span>
            <input type="date" value={value?.to || ''} className={selectCls} aria-label={`${label} to`}
              onChange={e => onChange({ ...value, to: e.target.value || undefined, empty: undefined })} />
          </>
        )}
      </span>
    </label>
  );
};

const selectCls = 'h-9 px-2 rounded-md border-[0.5px] border-primary-300 bg-white text-sm';

/** The Tagged / Without tags slot while no custom tag exists. */
const NoCustomTags: React.FC = () => {
  const [creating, setCreating] = useState(false);
  return (
    <div className="flex flex-col gap-1 text-xs text-grayscale-1100">
      Custom tags
      {creating ? (
        <div className="rounded-md border border-primary-300 bg-white p-2 min-w-[280px]">
          <NewTagForm onCreated={() => setCreating(false)} onCancel={() => setCreating(false)} />
        </div>
      ) : (
        <div className="h-9 flex items-center gap-2 px-2 rounded-md border-[0.5px] border-dashed border-primary-300 bg-white">
          <span className="text-grayscale-1100">None yet</span>
          <button type="button" onClick={() => setCreating(true)}
            className="text-primary-700 font-medium underline underline-offset-2">+ Create a tag</button>
          <span className="text-[11px] text-grayscale-1100"
            title="Explorer, Voyager (paid / assigned / trial / trial lapsed) and Prospect · invited are worked out automatically for every account">
            · lifecycle tags are in <b>Lifecycle</b>
          </span>
        </div>
      )}
    </div>
  );
};

export const TagFilter: React.FC<{
  value: TagFilterValue;
  onChange: (v: TagFilterValue, params: Record<string, string>) => void;
  /** Parts a tab cannot filter on (e.g. invite_sent where rows do not carry it). */
  hide?: FilterPart[];
  /** Show the per-option counts (all accounts, from the tags overview). */
  counts?: boolean;
}> = ({ value, onChange, hide = [], counts = true }) => {
  const opts = useTagOptions();
  const set = (patch: Partial<TagFilterValue>) => {
    const next = { ...value, ...patch };
    onChange(next, tagFilterParams(next));
  };
  const lcLabels = Object.fromEntries(opts.lifecycle.map(l =>
    [l.key, `${l.label}${counts && l.n !== undefined ? ` (${l.n.toLocaleString()})` : ''}`]));
  // Automatic cohort tags first (they always exist), then the custom ones.
  const allTags = [...opts.autoTags, ...opts.tags];
  const tagKeys = allTags.map(t => t.key);
  const tagLabels = Object.fromEntries(allTags.map(t =>
    [t.key, `${t.grp ? `${t.grp} › ` : ''}${t.label}${counts && t.n !== undefined ? ` (${t.n.toLocaleString()})` : ''}`]));
  const personaKeys = opts.personas.map(x => x.key);
  const personaLabels = Object.fromEntries(opts.personas.map(x =>
    [x.key, `${x.label}${counts && x.n !== undefined ? ` (${x.n.toLocaleString()})` : ''}`]));
  const show = (p: FilterPart) => !hide.includes(p);
  const active = tagFilterActive(value);
  const moreActive = !!(value.profileMin || value.profileMax || datesActive(value));
  const [more, setMore] = useState(moreActive);
  // No custom tag exists yet: the automatic tags still fill the pickers; a
  // small link lets the admin create the first custom one here.
  const noCustom = !opts.loading && !opts.error && opts.tags.length === 0;
  return (
    <div className="flex flex-wrap items-end gap-3">
      {show('lifecycle') && (
        <MultiSelect label="Lifecycle" options={LIFECYCLE_KEYS} labels={lcLabels}
          values={value.lifecycle} onChange={v => set({ lifecycle: v })} placeholder="Any lifecycle" />
      )}
      {show('persona') && (
        <MultiSelect label="Persona" options={personaKeys} labels={personaLabels}
          values={value.persona || []} onChange={v => set({ persona: v })} placeholder="Any persona" />
      )}
      {show('origin') && (
        <label className="flex flex-col gap-1 text-xs text-grayscale-1100"
          title="Self sign-up: the person made the account. Added by admin: the team did (panel, bulk add, import).">
          Account origin
          <select value={value.origin || ''} className={selectCls} onChange={e => set({ origin: e.target.value })}>
            <option value="">Any</option>
            {opts.origins.map(o => (
              <option key={o.key} value={o.key}>{o.label}{counts && o.n !== undefined ? ` (${o.n.toLocaleString()})` : ''}</option>
            ))}
          </select>
        </label>
      )}
      {show('tags') && (
        <div className="flex items-end gap-1">
          <MultiSelect label="Tagged" options={tagKeys} labels={tagLabels}
            values={value.tags} onChange={v => set({ tags: v })}
            placeholder="Any tags" />
          <select value={value.tagMode} aria-label="Match any or all of the tags"
            title="Has ANY of the picked tags, or ALL of them"
            onChange={e => set({ tagMode: e.target.value === 'all' ? 'all' : 'any' })}
            className={selectCls}>
            <option value="any">any of</option>
            <option value="all">all of</option>
          </select>
        </div>
      )}
      {show('not_tags') && (
        <MultiSelect label="Without tags" options={tagKeys} labels={tagLabels}
          values={value.notTags} onChange={v => set({ notTags: v })} placeholder="No exclusion" />
      )}
      {noCustom && (show('tags') || show('not_tags')) && <NoCustomTags />}
      {show('invite_sent') && (
        <label className="flex flex-col gap-1 text-xs text-grayscale-1100">
          Invite sent
          <select value={value.inviteSent} className={selectCls}
            onChange={e => set({ inviteSent: e.target.value as TriState })}>
            <option value="">Any</option><option value="1">Invite sent</option><option value="0">No invite</option>
          </select>
        </label>
      )}
      {show('signed_in') && (
        <label className="flex flex-col gap-1 text-xs text-grayscale-1100">
          Signed in
          <select value={value.signedIn} className={selectCls}
            onChange={e => set({ signedIn: e.target.value as TriState })}>
            <option value="">Any</option><option value="1">At least once</option><option value="0">Never</option>
          </select>
        </label>
      )}
      {(show('dates') || show('profile')) && (
        <button type="button" onClick={() => setMore(m => !m)}
          className={`text-xs pb-2 underline underline-offset-2 ${moreActive ? 'text-primary-700 font-semibold' : 'text-grayscale-1100'}`}>
          {more ? 'Fewer filters' : `More filters${moreActive ? ' (on)' : ''}`}
        </button>
      )}
      {active && (
        <button type="button" onClick={() => onChange(EMPTY_TAG_FILTER, {})}
          className="text-xs text-grayscale-1100 underline underline-offset-2 pb-2">clear tag filters</button>
      )}
      {opts.error && <span className="text-[11px] text-red-600 pb-2">Tag options: {opts.error}</span>}
      {more && (show('dates') || show('profile')) && (
        <div className="basis-full flex flex-wrap items-end gap-3 rounded-lg border border-grayscale-300 bg-grayscale-50 p-2">
          {show('dates') && DATE_FACTS.map(f => (
            <DateFactFilter key={f.key} label={f.label} value={value.dates?.[f.key]}
              onChange={d => { const dates = { ...(value.dates || {}) }; if (d) dates[f.key] = d; else delete dates[f.key]; set({ dates }); }} />
          ))}
          {show('profile') && (
            <label className="flex flex-col gap-1 text-xs text-grayscale-1100">
              Profile complete (%)
              <span className="flex items-center gap-1">
                <input type="number" min={0} max={100} placeholder="min" value={value.profileMin} className={`${selectCls} w-20`}
                  onChange={e => set({ profileMin: e.target.value })} />
                <span>–</span>
                <input type="number" min={0} max={100} placeholder="max" value={value.profileMax} className={`${selectCls} w-20`}
                  onChange={e => set({ profileMax: e.target.value })} />
              </span>
            </label>
          )}
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Row selection — ticked rows survive paging; "all N matching" is a mode
// ---------------------------------------------------------------------------
export type RowSelection = {
  /** user_id → email of every ticked row (the email travels because the row
   *  may no longer be on screen). */
  picked: Record<string, string>;
  ids: string[];
  count: number;
  allMatching: boolean;
  setAllMatching: (v: boolean) => void;
  isPicked: (row: any) => boolean;
  toggle: (row: any) => void;
  allOnPage: (rows: any[]) => boolean;
  togglePage: (rows: any[]) => void;
  unpick: (ids: string[]) => void;
  clear: () => void;
};

export function useRowSelection(opts: {
  idKey?: string; emailKey?: string;
  /** Changing this (e.g. the filter's JSON) drops the selection — ticked rows
   *  and "all matching" — so a bulk action never reaches accounts the new
   *  filter no longer shows. Paging keeps it. */
  resetKey?: string;
} = {}): RowSelection {
  const idKey = opts.idKey || 'user_id';
  const emailKey = opts.emailKey || 'email';
  const [picked, setPicked] = useState<Record<string, string>>({});
  const [allMatching, setAllMatching] = useState(false);
  useEffect(() => { setAllMatching(false); setPicked({}); }, [opts.resetKey]);
  const idOf = useCallback((r: any) => String(r?.[idKey] ?? ''), [idKey]);
  const ids = Object.keys(picked);
  const isPicked = (r: any) => allMatching || !!picked[idOf(r)];
  const toggle = (r: any) => {
    const id = idOf(r);
    if (!id) return;
    setAllMatching(false);
    setPicked(s => {
      const next = { ...s };
      if (id in next) delete next[id]; else next[id] = String(r?.[emailKey] ?? '');
      return next;
    });
  };
  const pageIds = (rows: any[]) => rows.map(idOf).filter(Boolean);
  const allOnPage = (rows: any[]) => {
    const p = pageIds(rows);
    return p.length > 0 && p.every(id => id in picked);
  };
  const togglePage = (rows: any[]) => {
    setAllMatching(false);
    if (allOnPage(rows)) {
      setPicked(s => { const n = { ...s }; pageIds(rows).forEach(id => delete n[id]); return n; });
    } else {
      setPicked(s => {
        const n = { ...s };
        rows.forEach(r => { const id = idOf(r); if (id) n[id] = String(r?.[emailKey] ?? ''); });
        return n;
      });
    }
  };
  const unpick = (list: string[]) =>
    setPicked(s => { const n = { ...s }; list.forEach(id => delete n[id]); return n; });
  const clear = () => { setPicked({}); setAllMatching(false); };
  return { picked, ids, count: ids.length, allMatching, setAllMatching, isPicked, toggle,
    allOnPage, togglePage, unpick, clear };
}

/** "Select page · Select all N matching · Clear" for a paged list. */
export const SelectionControls: React.FC<{
  sel: RowSelection; rows: any[];
  /** Server total for the current filter; enables "Select all N matching". */
  total?: number;
}> = ({ sel, rows, total }) => (
  <div className="flex items-center gap-2 flex-wrap text-xs text-grayscale-1100">
    <Btn small onClick={() => sel.togglePage(rows)} disabled={!rows.length}>
      {sel.allOnPage(rows) && !sel.allMatching ? 'Unselect page' : `Select page (${rows.length})`}
    </Btn>
    {total !== undefined && total > 0 && (
      <Btn small kind={sel.allMatching ? 'primary' : 'subtle'}
        onClick={() => sel.setAllMatching(!sel.allMatching)}
        title="Everything the current filters match, on every page">
        {sel.allMatching ? `All ${total.toLocaleString()} matching selected` : `Select all ${total.toLocaleString()} matching`}
      </Btn>
    )}
    {(sel.count > 0 || sel.allMatching) && (
      <Btn small onClick={sel.clear}>Clear ({sel.allMatching ? (total ?? 0).toLocaleString() : sel.count})</Btn>
    )}
  </div>
);

export const RowCheckbox: React.FC<{ sel: RowSelection; row: any; label?: string }> = ({ sel, row, label }) => (
  <input type="checkbox" className="cursor-pointer accent-primary-500"
    checked={sel.isPicked(row)} disabled={sel.allMatching}
    onChange={() => sel.toggle(row)}
    aria-label={`Select ${label || row?.email || row?.user_id || 'row'}`}
    title={sel.allMatching ? 'Every matching account is selected' : 'Select for bulk actions'} />
);

// ---------------------------------------------------------------------------
// Tag write helpers
// ---------------------------------------------------------------------------
export type TagWriteResult = {
  assigned?: number; already?: number; users?: number; unknown_emails?: string[]; removed?: number;
};

export async function saveTagDef(body: {
  key?: string; label: string; color: string; description?: string; grp?: string;
}): Promise<{ row: TagOption | null; error: string }> {
  const { data, error } = await panelPost(TAGS_SECTION, 'def_save', body);
  if (error) return { row: null, error };
  invalidateTagOptions();
  const r = data?.row || data;
  return { row: r ? { ...r, color: safeHex(r.color) } : null, error: '' };
}

/** assign / unassign — exactly one of user_ids, emails, filter(+expected_count). */
export async function writeTags(op: 'assign' | 'unassign', body: {
  tag_keys: string[]; user_ids?: string[]; emails?: string[];
  filter?: Record<string, string>; expected_count?: number; note?: string;
}): Promise<{ data: TagWriteResult | null; error: string }> {
  const { data, error } = await panelPost(TAGS_SECTION, op, body);
  if (!error) invalidateTagOptions();
  return { data: error ? null : (data || {}), error };
}

// ---------------------------------------------------------------------------
// Inline "new tag" form
// ---------------------------------------------------------------------------
export const ColorPicker: React.FC<{ value: string; onChange: (c: string) => void }> = ({ value, onChange }) => (
  <div className="flex items-center gap-1 flex-wrap">
    {TAG_COLORS.map(c => (
      <button key={c} type="button" onClick={() => onChange(c)} title={c}
        aria-label={`Colour ${c}`}
        className={`w-5 h-5 rounded-full border-2 ${value.toLowerCase() === c.toLowerCase() ? 'border-grayscale-1300' : 'border-white'}`}
        style={{ backgroundColor: c }} />
    ))}
    <input type="color" value={safeHex(value)} onChange={e => onChange(e.target.value.toUpperCase())}
      title="Any colour" className="w-7 h-6 p-0 border-0 bg-transparent cursor-pointer" />
  </div>
);

export const NewTagForm: React.FC<{ onCreated: (t: TagOption) => void; onCancel?: () => void }> =
  ({ onCreated, onCancel }) => {
  const [label, setLabel] = useState('');
  const [color, setColor] = useState(TAG_COLORS[0]);
  const [grp, setGrp] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const create = async () => {
    const l = label.trim();
    if (!l) return;
    setBusy(true); setErr('');
    const { row, error } = await saveTagDef({ label: l.slice(0, 60), color, grp: grp.trim().slice(0, 40) || undefined });
    setBusy(false);
    if (error || !row) { setErr(error || 'Could not create the tag'); return; }
    setLabel(''); setGrp('');
    onCreated(row);
  };
  return (
    <div className="flex flex-col gap-2 rounded-lg border border-grayscale-300 bg-grayscale-100 p-2">
      <div className="flex items-end gap-2 flex-wrap">
        <label className="flex flex-col gap-1 text-xs text-grayscale-1100">
          New tag
          <input value={label} maxLength={60} autoFocus placeholder="e.g. Webinar Oct 2026"
            onChange={e => setLabel(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') create(); }}
            className="h-8 px-2 rounded-md border-[0.5px] border-primary-300 bg-white text-sm w-52" />
        </label>
        <label className="flex flex-col gap-1 text-xs text-grayscale-1100">
          Group (optional)
          <input value={grp} maxLength={40} placeholder="e.g. Campaigns"
            onChange={e => setGrp(e.target.value)}
            className="h-8 px-2 rounded-md border-[0.5px] border-primary-300 bg-white text-sm w-36" />
        </label>
        <ColorPicker value={color} onChange={setColor} />
        <Btn small kind="primary" onClick={create} disabled={busy || !label.trim()}>
          {busy ? 'Creating…' : 'Create tag'}
        </Btn>
        {onCancel && <Btn small onClick={onCancel}>Cancel</Btn>}
      </div>
      {err && <Msg kind="error">{err}</Msg>}
    </div>
  );
};

// ---------------------------------------------------------------------------
// BulkTagBar — add / remove tags on a selection, a whole filter, or a pasted list
// ---------------------------------------------------------------------------
export const BulkTagBar: React.FC<{
  /** Ticked rows. */
  userIds?: string[];
  /** Pasted addresses (resolved server-side; unknown ones are reported). */
  emails?: string[];
  /** "All N matching": the accounts params and the count the operator saw. Wins over userIds. */
  filter?: { params: Record<string, string>; count: number; summary?: string[] } | null;
  onDone?: (res: TagWriteResult, op: 'assign' | 'unassign') => void;
  onClear?: () => void;
  title?: string;
}> = ({ userIds = [], emails = [], filter, onDone, onClear, title }) => {
  const opts = useTagOptions();
  const [keys, setKeys] = useState<string[]>([]);
  const [creating, setCreating] = useState(false);
  const [confirm, setConfirm] = useState<'assign' | 'unassign' | null>(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ kind: 'ok' | 'error'; text: string } | null>(null);

  const mode: 'filter' | 'ids' | 'emails' | 'none' =
    filter ? 'filter' : userIds.length ? 'ids' : emails.length ? 'emails' : 'none';
  const n = mode === 'filter' ? filter!.count : mode === 'ids' ? userIds.length : emails.length;
  const who = mode === 'filter'
    ? `all ${n.toLocaleString()} account${n === 1 ? '' : 's'} matching the filters`
    : mode === 'ids' ? `${n.toLocaleString()} selected account${n === 1 ? '' : 's'}`
      : mode === 'emails' ? `${n.toLocaleString()} pasted address${n === 1 ? '' : 'es'}` : 'nobody yet';
  const label = (k: string) => opts.tagByKey[k]?.label || k;
  const picked: TagRef[] = keys.map(k => opts.tagByKey[k] || { key: k, label: k });
  // A result belongs to the target it was run on: a new selection or filter clears it
  // (the count alone is not the key — it moves when the list reloads after a write).
  const targetSig = `${mode}|${JSON.stringify(filter?.params || null)}|${userIds.join(',')}|${emails.join(',')}`;
  useEffect(() => { setMsg(null); }, [targetSig]);

  const run = async () => {
    const op = confirm;
    if (!op) return;
    setConfirm(null); setBusy(true); setMsg(null);
    const body: Parameters<typeof writeTags>[1] = { tag_keys: keys };
    if (mode === 'filter') { body.filter = filter!.params; body.expected_count = filter!.count; }
    else if (mode === 'ids') body.user_ids = userIds;
    else body.emails = emails;
    const { data, error } = await writeTags(op, body);
    setBusy(false);
    if (error || !data) { setMsg({ kind: 'error', text: error || 'Request failed' }); return; }
    const unknown = data.unknown_emails || [];
    const text = op === 'assign'
      ? `Added ${Number(data.assigned || 0).toLocaleString()} tag assignment(s) across `
        + `${Number(data.users ?? n).toLocaleString()} account(s)`
        + (data.already ? `; ${Number(data.already).toLocaleString()} were already in place` : '') + '.'
      : `Removed ${Number(data.removed || 0).toLocaleString()} tag assignment(s).`;
    setMsg({ kind: 'ok', text: text + (unknown.length
      ? ` ${unknown.length} address(es) are not accounts: ${unknown.slice(0, 8).join(', ')}${unknown.length > 8 ? ' …' : ''}`
      : '') });
    onDone?.(data, op);
  };

  const ready = mode !== 'none' && keys.length > 0 && !busy && n > 0;
  return (
    <div className="flex flex-col gap-2 rounded-xl border border-primary-300 bg-primary-100/30 p-3">
      <div className="flex items-end gap-3 flex-wrap">
        <div className="flex flex-col gap-0.5 min-w-[10rem]">
          <span className="text-xs font-bold text-grayscale-1300 font-grotesk">{title || 'Tags'}</span>
          <span className="text-xs text-grayscale-1200">For {who}</span>
        </div>
        <MultiSelect label="Tags to add or remove" options={opts.tags.map(t => t.key)}
          labels={Object.fromEntries(opts.tags.map(t => [t.key, `${t.grp ? `${t.grp} › ` : ''}${t.label}`]))}
          values={keys} onChange={setKeys}
          placeholder={opts.tags.length ? 'Pick tags…' : 'No tags yet — create one'} />
        <Btn small onClick={() => setCreating(c => !c)}>{creating ? 'Close' : '+ New tag'}</Btn>
        <Btn small kind="primary" disabled={!ready} onClick={() => setConfirm('assign')}>
          {busy ? 'Working…' : 'Add tags'}
        </Btn>
        <Btn small disabled={!ready} onClick={() => setConfirm('unassign')}>Remove tags</Btn>
        {onClear && mode !== 'none' && <Btn small onClick={onClear}>Clear selection</Btn>}
      </div>
      {picked.length > 0 && <TagPills tags={picked} onRemove={k => setKeys(ks => ks.filter(x => x !== k))} />}
      {creating && (
        <NewTagForm onCancel={() => setCreating(false)}
          onCreated={t => { setKeys(ks => (ks.includes(t.key) ? ks : [...ks, t.key])); setCreating(false); }} />
      )}
      {mode === 'none' && (
        <div className="text-[11px] text-grayscale-1100">Tick rows (or select all matching) to tag them.</div>
      )}
      {msg && <Msg kind={msg.kind}>{msg.text}</Msg>}
      {confirm && (
        <Modal
          title={`${confirm === 'assign' ? 'Add' : 'Remove'} ${keys.length} tag${keys.length === 1 ? '' : 's'} ${confirm === 'assign' ? 'to' : 'from'} ${n.toLocaleString()} account${n === 1 ? '' : 's'}?`}
          onClose={() => setConfirm(null)}
          footer={<>
            <Btn onClick={() => setConfirm(null)}>Cancel</Btn>
            <Btn kind={confirm === 'assign' ? 'primary' : 'danger'} onClick={run}>
              {confirm === 'assign' ? 'Add' : 'Remove'} for {n.toLocaleString()}
            </Btn>
          </>}>
          <TagPills tags={picked} />
          <p>
            {confirm === 'assign' ? 'Adds' : 'Removes'} {keys.map(label).map(s => `“${s}”`).join(', ')}
            {' '}{confirm === 'assign' ? 'to' : 'from'} {who}.
            {confirm === 'assign' ? ' Accounts that already have a tag keep it once.' : ''}
          </p>
          {mode === 'filter' && (
            <>
              {(filter!.summary || []).length > 0 && (
                <ul className="text-xs text-grayscale-1100 list-disc pl-5">
                  {filter!.summary!.map((s, i) => <li key={i}>{s}</li>)}
                </ul>
              )}
              <p className="text-[11px] text-grayscale-1100">
                The server recounts these filters and refuses if the number is no longer
                {' '}{n.toLocaleString()} — refresh the list and try again if that happens.
              </p>
            </>
          )}
          {mode === 'emails' && (
            <p className="text-[11px] text-grayscale-1100">
              Addresses that are not accounts are skipped and listed afterwards.
            </p>
          )}
        </Modal>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// UserTagEditor — one account's lifecycle + tags with inline add / remove
// ---------------------------------------------------------------------------
export const UserTagEditor: React.FC<{
  userId: string; email?: string;
  /** What the caller already knows (a decorated row), shown until the fresh read lands. */
  initial?: Record<string, any> | null;
  onChanged?: () => void;
}> = ({ userId, email, initial, onChanged }) => {
  const opts = useTagOptions();
  const [row, setRow] = useState<Record<string, any> | null>(initial || null);
  const [adding, setAdding] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  // A fresh read by e-mail (team included), so the header is right even when
  // the row it was opened from is stale or undecorated.
  const refresh = useCallback(async () => {
    if (!email) return;
    const { data } = await panelGet(TAGS_SECTION, 'accounts',
      { q: email, exclude_team: '0', per_page: 25, page: 0 });
    const hit = (data?.rows || []).find((r: any) => String(r.user_id) === String(userId));
    if (hit) setRow(hit);
  }, [userId, email]);
  useEffect(() => { setRow(initial || null); refresh(); }, [userId, refresh]); // eslint-disable-line react-hooks/exhaustive-deps

  const tags: TagRef[] = Array.isArray(row?.tags) ? row!.tags : [];
  const have = new Set(tags.map(t => t.key));
  const write = async (op: 'assign' | 'unassign', keys: string[]) => {
    if (!keys.length) return;
    setBusy(true); setErr('');
    const { error } = await writeTags(op, { tag_keys: keys, user_ids: [userId] });
    setBusy(false);
    if (error) { setErr(error); return; }
    // Reflect it at once from the defs, then confirm from the server.
    setRow(r => {
      const cur: TagRef[] = Array.isArray(r?.tags) ? r!.tags : [];
      const next = op === 'assign'
        ? [...cur, ...keys.filter(k => !cur.some(t => t.key === k))
          .map(k => ({ key: k, label: opts.tagByKey[k]?.label || k, color: opts.tagByKey[k]?.color }))]
        : cur.filter(t => !keys.includes(t.key));
      return { ...(r || {}), tags: next };
    });
    setAdding([]);
    refresh();
    onChanged?.();
  };
  const addable = opts.tags.filter(t => !have.has(t.key));
  return (
    <div className="flex flex-wrap items-end gap-2">
      <span className="inline-flex flex-wrap items-center gap-1 pb-1.5">
        <LifecyclePill value={row?.lifecycle ?? null} subtag={row?.subtag} label={row?.lifecycle_label} />
        <OriginPill value={row?.account_origin} label={row?.account_origin_label} via={row?.account_origin_via_label} />
        {row && (
          <select aria-label="Account origin" title="Where this account came from — set it by hand if it is wrong"
            value={row.account_origin_via === 'manual' ? String(row.account_origin || '') : ''} disabled={busy}
            onChange={async e => {
              setBusy(true); setErr('');
              const { error } = await panelPost(TAGS_SECTION, 'origin_override',
                { user_id: userId, origin: e.target.value, note: 'Set on the account' });
              setBusy(false);
              if (error) { setErr(error); return; }
              invalidateTagOptions(); refresh(); onChanged?.();
            }}
            className="h-6 px-1 rounded border-[0.5px] border-primary-300 bg-white text-[11px] text-grayscale-1200">
            <option value="">Origin: automatic</option>
            <option value="self">Origin: self sign-up</option>
            <option value="admin">Origin: added by admin</option>
          </select>
        )}
        {row?.invite_sent === true && (
          <span className="text-[11px] text-grayscale-1100" title="An invite record exists for this account">invite sent</span>
        )}
        <TagPills tags={tags} onRemove={busy ? undefined : k => write('unassign', [k])}
          empty={<span className="text-[11px] text-grayscale-1100">no tags</span>} />
      </span>
      <MultiSelect label="Add tags" options={addable.map(t => t.key)}
        labels={Object.fromEntries(addable.map(t => [t.key, `${t.grp ? `${t.grp} › ` : ''}${t.label}`]))}
        values={adding} onChange={setAdding}
        placeholder={opts.tags.length ? 'Pick…' : 'No tags defined'} />
      <Btn small kind="primary" disabled={busy || !adding.length} onClick={() => write('assign', adding)}>
        {busy ? 'Saving…' : 'Add'}
      </Btn>
      {err && <span className="text-xs text-red-600 pb-1.5">{err}</span>}
    </div>
  );
};
